import React, { useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Bell, ChevronLeft, ChevronRight, FlaskConical, BookUser, IdCard, FolderOpen, Stethoscope, AlertTriangle, Info, Users } from 'lucide-react';
import { Contacts } from './Library/Contacts';
import { Hygiene } from './Library/Hygiene';
import { Exercise } from './Library/Exercise';
import { Nutrition } from './Library/Nutrition';
import { Sleep } from './Library/Sleep';
import { Wellbeing } from './Library/Wellbeing';
import { LabGlossary } from './Library/LabGlossary';
import { MedicalCard } from './Library/MedicalCard';
import { Vault } from './Library/Vault';
import { HowToVitals } from './Library/HowToVitals';
import { Emergency } from './Library/Emergency';
import CoCarers from './CoCarers';
import { NotificationCenter } from '../components/NotificationCenter';
import { useLanguage } from '../functions/useLanguage';
import { useBean } from '../context/BeanContext';
import { type TranslationKey } from '../functions/i18n';

type Category = 'contacts' | 'medical_card' | 'co_carers' | 'wellbeing' | 'hygiene' | 'exercise' | 'sleep' | 'nutrition' | 'lab_glossary' | 'vault' | 'how_to' | 'emergency' | 'reminders';

const ARTICLE_CATEGORIES: Category[] = ['hygiene', 'exercise', 'nutrition', 'sleep', 'wellbeing'];

// Some categories reuse image files from a previous name — map category → image base name
const ARTICLE_IMAGE_KEY: Partial<Record<Category, string>> = {
  wellbeing: 'obstacles',
  sleep:     'medication',
};

// Animal-focused photos for article cards
const ARTICLE_ANIMAL_PHOTO: Partial<Record<Category, string>> = {
  hygiene:   '/images/guide/animal-hygiene.jpg',   // dog being groomed
  exercise:  '/images/guide/animal-exercise.jpg',  // dog running/playing
  nutrition:      'https://images.unsplash.com/vector-1748448815232-0e1a737114d1?q=80&w=2148&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
  wellbeing: '/images/guide/animal-wellbeing.jpg', // happy dog portrait
  sleep:     '/images/guide/animal-sleep.jpg',     // sleeping cat
};

const READ_TIMES: Partial<Record<Category, string>> = {
  hygiene:   '8',
  exercise:  '8',
  nutrition:      '9',
  wellbeing: '8',
  sleep:     '7',
};

const SLUG_TO_CATEGORY: Record<string, Category> = {
  contacts:       'contacts',
  'medical-card': 'medical_card',
  'co-carers':    'co_carers',
  'how-to':       'how_to',
  emergency:      'emergency',
  hygiene:        'hygiene',
  exercise:       'exercise',
  wellbeing:      'wellbeing',
  nutrition:      'nutrition',
  sleep:          'sleep',
  'lab-glossary': 'lab_glossary',
  'medical-files': 'vault',
  reminders:      'reminders',
};

const CATEGORY_TO_SLUG: Record<Category, string> = {
  contacts:     'contacts',
  medical_card: 'medical-card',
  co_carers:    'co-carers',
  how_to:       'how-to',
  emergency:    'emergency',
  hygiene:      'hygiene',
  exercise:     'exercise',
  wellbeing:    'wellbeing',
  nutrition:         'nutrition',
  sleep:        'sleep',
  lab_glossary: 'lab-glossary',
  vault:        'medical-files',
  reminders:    'reminders',
};

export default function Library() {
  const { section } = useParams<{ section: string }>();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { awardXP } = useBean();
  const carouselRef = useRef<HTMLDivElement>(null);

  const catText = (cat: Category) => {
    if (cat === 'co_carers') {
      return {
        title: t('members.coCarers' as TranslationKey),
        subtitle: t('members.coCarersSubtitle' as TranslationKey),
      };
    }

    return {
      title:    t(`guide.category.${cat}.title`    as TranslationKey),
      subtitle: t(`guide.category.${cat}.subtitle` as TranslationKey),
    };
  };

  const view: Category | null = section ? (SLUG_TO_CATEGORY[section.toLowerCase()] ?? null) : null;

  const navigateTo = (cat: Category | null) => {
    if (cat) navigate(`/library/${CATEGORY_TO_SLUG[cat]}`);
    else navigate('/library');
  };

  const scrollCarousel = () => {
    carouselRef.current?.scrollBy({ left: 210, behavior: 'smooth' });
  };

  if (view !== null) {
    const viewTitle = catText(view).title;
    const viewSubtitle = catText(view).subtitle;
    return (
      <div className="w-full px-4 pt-4 pb-[80px] min-h-screen">
        <div className="mb-6">
          <button
            onClick={() => navigateTo(null)}
            className="inline-flex items-center gap-2 text-sm font-semibold text-primary-brown hover:text-primary-brown/80 transition-colors mb-3"
          >
            <ChevronLeft size={16} />
            {t('nav.guide')}
          </button>
          <h1 className={`text-2xl font-black ${view === 'emergency' ? 'text-error-red' : 'text-[#0d2744]'}`}>{viewTitle}</h1>
          {viewSubtitle && view !== 'emergency' && <p className="text-sm text-gray-500 mt-1">{viewSubtitle}</p>}
        </div>
        {view === 'contacts'     && <Contacts />}
        {view === 'hygiene'      && <Hygiene />}
        {view === 'exercise'     && <Exercise />}
        {view === 'wellbeing'    && <Wellbeing />}
        {view === 'nutrition'    && <Nutrition />}
        {view === 'sleep'        && <Sleep />}
        {view === 'lab_glossary' && <LabGlossary />}
        {view === 'medical_card' && <MedicalCard />}
        {view === 'co_carers'   && <CoCarers embedded />}
        {view === 'vault'        && <Vault />}
        {view === 'how_to'       && <HowToVitals />}
        {view === 'emergency'    && <Emergency />}
        {view === 'reminders'    && (
          <div className="w-full max-w-5xl mx-auto space-y-6">
            <NotificationCenter
              embedded
              initService={false}
              title={t('notifications.allReminders')}
              panelClassName="bg-white border border-gray-200"
            />
            {/* Info: reminders are stored locally and restored from cloud */}
            <div className="flex items-start gap-2 px-1 pb-2 text-xs text-text-secondary/60">
              <Info size={13} className="flex-shrink-0 mt-0.5" />
              <span>{t('notifications.storageNote')}</span>
            </div>
          </div>
        )}

      </div>
    );
  }

  return (
    <div className="w-full px-4 pt-4 pb-[80px] min-h-screen">

      {/* Resources */}
      <div className="mb-6">
        <h1 className="text-2xl font-black text-[#0d2744]">{t('nav.guide')}</h1>
        <p className="text-sm text-gray-500 mt-1">{t('guide.subtitle')}</p>
      </div>
      <div className="grid grid-cols-1 gap-4" data-tour="guide-resources">

        {/* Emergency Vet Finder */}
        <button
          onClick={() => navigateTo('emergency')}
          className="w-full text-left bg-error-red/5 border border-error-red/20 rounded-3xl px-4 py-3 hover:bg-error-red/10 active:bg-error-red/10 transition-colors"
        >
          <div className="flex items-center justify-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-error-red/10 flex items-center justify-center shrink-0">
              <AlertTriangle size={18} className="text-error-red" />
            </div>
            <p className="text-base font-bold text-error-red">{catText('emergency').title}</p>
          </div>
        </button>

        {/* Pet ID Card */}
        <button
          onClick={() => navigateTo('medical_card')}
          className="w-full text-left bg-white border border-slate-100 rounded-3xl p-6 hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary-brown/10 flex items-center justify-center shrink-0">
                <IdCard size={18} className="text-primary-brown" />
              </div>
              <div>
                <p className="text-base font-bold text-[#0d2744]">{catText('medical_card').title}</p>
                <p className="text-sm text-gray-500 mt-1">{catText('medical_card').subtitle}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-xs font-bold text-primary-brown bg-primary-brown/10 rounded-full px-2.5 py-1">+25 XP</span>
              <ChevronRight size={18} className="text-gray-400" />
            </div>
          </div>
        </button>

        {/* Care Team */}
        <button
          onClick={() => navigateTo('contacts')}
          className="w-full text-left bg-white border border-slate-100 rounded-3xl p-6 hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary-brown/10 flex items-center justify-center shrink-0">
                <BookUser size={18} className="text-primary-brown" />
              </div>
              <div>
                <p className="text-base font-bold text-[#0d2744]">{catText('contacts').title}</p>
                <p className="text-sm text-gray-500 mt-1">{catText('contacts').subtitle}</p>
              </div>
            </div>
            <ChevronRight size={18} className="text-gray-400 shrink-0" />
          </div>
        </button>

        {/* Co-Carers */}
        <button
          onClick={() => navigateTo('co_carers')}
          className="w-full text-left bg-white border border-slate-100 rounded-3xl p-6 hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary-brown/10 flex items-center justify-center shrink-0">
                <Users size={18} className="text-primary-brown" />
              </div>
              <div>
                <p className="text-base font-bold text-[#0d2744]">{catText('co_carers').title}</p>
                <p className="text-sm text-gray-500 mt-1">{catText('co_carers').subtitle}</p>
              </div>
            </div>
            <ChevronRight size={18} className="text-gray-400 shrink-0" />
          </div>
        </button>

        {/* Vet Records */}
        <button
          onClick={() => navigateTo('vault')}
          className="w-full text-left bg-white border border-slate-100 rounded-3xl p-6 hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary-brown/10 flex items-center justify-center shrink-0">
                <FolderOpen size={18} className="text-primary-brown" />
              </div>
              <div>
                <p className="text-base font-bold text-[#0d2744]">{catText('vault').title}</p>
                <p className="text-sm text-gray-500 mt-1">{catText('vault').subtitle}</p>
              </div>
            </div>
            <ChevronRight size={18} className="text-gray-400 shrink-0" />
          </div>
        </button>

        {/* Reminders */}
        <button
          onClick={() => navigateTo('reminders')}
          className="w-full text-left bg-white border border-slate-100 rounded-3xl p-6 hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary-brown/10 flex items-center justify-center shrink-0">
                <Bell size={18} className="text-primary-brown" />
              </div>
              <div>
                <p className="text-base font-bold text-[#0d2744]">{catText('reminders').title}</p>
                <p className="text-sm text-gray-500 mt-1">{catText('reminders').subtitle}</p>
              </div>
            </div>
            <ChevronRight size={18} className="text-gray-400 shrink-0" />
          </div>
        </button>

        {/* Blood Tests Explained */}
        <button
          onClick={async () => {
            await awardXP('lab_glossary');
            navigate('/library/lab-glossary');
          }}
          className="w-full text-left bg-white border border-slate-100 rounded-3xl p-6 hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary-brown/10 flex items-center justify-center shrink-0">
                <FlaskConical size={18} className="text-primary-brown" />
              </div>
              <div>
                <p className="text-base font-bold text-[#0d2744]">{catText('lab_glossary').title}</p>
                <p className="text-sm text-gray-500 mt-1">{catText('lab_glossary').subtitle}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-xs font-bold text-primary-brown bg-primary-brown/10 rounded-full px-2.5 py-1">+25 XP</span>
              <ChevronRight size={18} className="text-gray-400" />
            </div>
          </div>
        </button>

        {/* How to: Check Vitals */}
        <button
          onClick={() => navigateTo('how_to')}
          className="w-full text-left bg-white border border-slate-100 rounded-3xl p-6 hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary-brown/10 flex items-center justify-center shrink-0">
                <Stethoscope size={18} className="text-primary-brown" />
              </div>
              <div>
                <p className="text-base font-bold text-[#0d2744]">{catText('how_to').title}</p>
                <p className="text-sm text-gray-500 mt-1">{catText('how_to').subtitle}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-xs font-bold text-primary-brown bg-primary-brown/10 rounded-full px-2.5 py-1">+25 XP</span>
              <ChevronRight size={18} className="text-gray-400" />
            </div>
          </div>
        </button>


      </div>

      {/* Articles */}
      <div className="bg-white rounded-t-3xl md:rounded-none pt-4 pb-3 overflow-hidden">
        <div className="flex items-center mb-4 px-4 gap-2">
          <h2 className="text-base font-bold text-respiratory-blue">{t('guide.articles')}</h2>
          <button
            onClick={scrollCarousel}
            className="w-6 h-6 rounded-full bg-respiratory-blue flex items-center justify-center flex-shrink-0 transition-opacity active:opacity-70 lg:hidden"
          >
            <ChevronRight size={13} className="text-white" />
          </button>
        </div>

        <div
          ref={carouselRef}
          data-tour="articles-carousel"
          className="flex gap-8 overflow-x-auto scrollbar-hide px-4 pb-8 lg:justify-center lg:overflow-x-visible lg:flex-wrap"
          style={{ WebkitOverflowScrolling: 'touch' } as React.CSSProperties}
        >
          {ARTICLE_CATEGORIES.map(cat => {
            const text = catText(cat);
            const imgKey = ARTICLE_IMAGE_KEY[cat] ?? cat;
            const animalPhoto = ARTICLE_ANIMAL_PHOTO[cat];
            return (
              <button
                key={cat}
                onClick={async () => {
                  await awardXP('guide_article');
                  navigateTo(cat);
                }}
                className="flex-shrink-0 w-[190px] rounded-2xl overflow-hidden text-left active:scale-[0.97] transition-transform bg-white"
              >
                <div className="rounded-2xl overflow-hidden bg-white shadow-sm">
                {/* Illustration */}
                <div className="relative w-full h-[200px]">
                  <img
                    src={animalPhoto ?? `/images/guide/article-${imgKey}.svg`}
                    onError={e => {
                      const el = e.currentTarget as HTMLImageElement;
                      if (animalPhoto && el.src === animalPhoto) {
                        el.src = `/images/guide/article-${imgKey}.svg`;
                      } else {
                        el.src = `/images/guide/card-${imgKey}.jpg`;
                      }
                    }}
                    alt={text.title}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  {/* Subtle fade at bottom edge into white */}
                  <div className="absolute bottom-0 inset-x-0 h-36 bg-gradient-to-t from-white to-transparent" />
                </div>
                {/* Text below illustration */}
                <div className="relative z-10 px-3 pb-4 -mt-10">
                  <p className="font-black text-respiratory-blue text-3xl leading-tight text-center">{text.title}</p>
                  <p className="text-xs text-text-secondary mt-1 leading-snug line-clamp-2 text-center">{text.subtitle}</p>
                  <p className="flex mt-2 items-center justify-center gap-3">
                    <span className="text-[11px] text-gray-400">{READ_TIMES[cat]} {t('guide.minRead')}</span>
                    <span className="text-[11px] font-bold text-respiratory-blue bg-respiratory-blue/15 rounded-full px-2.5 py-0.5">+10 XP</span>
                  </p>
                </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
